import { useEffect, useState } from "react"
import { DataTable } from "@/components/ui/DataTable"
import { Button } from "@/components/ui/button"
import { ChevronLeft, ChevronRight, ChevronsLeft, Loader2, FileText, ExternalLink } from "lucide-react"
import { BillSearchInput } from "@/components/BillSearchInput"
import { type DatasourceBill } from '@/api/datasource-bills-api'
import { getDatasourceBillsColumns } from "./datasourceBillsColumns"
import { BillFullTextDialog } from "./BillFullTextDialog"
import { BillVersionsDialog } from "./BillVersionsDialog"

interface DatasourceBillsTableProps {
  bills: DatasourceBill[]
  currentPage: number
  pageSize: number
  totalPages: number
  onPageChange: (page: number) => void
  searchQuery: string
  onSearchChange: (value: string) => void
  isLoading: boolean
  totalFilteredItems: number
  allBillsLoaded?: boolean
}

export function DatasourceBillsTable({
  bills = [],
  currentPage,
  pageSize,
  totalPages,
  onPageChange,
  searchQuery,
  onSearchChange,
  isLoading,
  totalFilteredItems,
  allBillsLoaded = false
}: DatasourceBillsTableProps) {
  const [isMobile, setIsMobile] = useState(false)
  const [selectedBillForFullText, setSelectedBillForFullText] = useState<DatasourceBill | null>(null)
  const [fullTextDialogOpen, setFullTextDialogOpen] = useState(false)
  const [selectedBillForVersions, setSelectedBillForVersions] = useState<DatasourceBill | null>(null)
  const [versionsDialogOpen, setVersionsDialogOpen] = useState(false)

  // Update mobile state on resize
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768) // 768px is the md breakpoint in Tailwind
    }

    checkIfMobile() // Check initially

    window.addEventListener('resize', checkIfMobile)
    return () => window.removeEventListener('resize', checkIfMobile)
  }, [])

  const handleViewFullText = (bill: DatasourceBill) => {
    setSelectedBillForFullText(bill)
    setFullTextDialogOpen(true)
  }

  const handleCloseFullTextDialog = (open: boolean) => {
    setFullTextDialogOpen(open)
    if (!open) {
      setSelectedBillForFullText(null)
    }
  }

  const handleViewVersions = (bill: DatasourceBill) => {
    setSelectedBillForVersions(bill)
    setVersionsDialogOpen(true)
  }

  const handleCloseVersionsDialog = (open: boolean) => {
    setVersionsDialogOpen(open)
    if (!open) {
      setSelectedBillForVersions(null)
    }
  }

  const columns = getDatasourceBillsColumns({
    onViewVersions: handleViewVersions
  })

  const startItem = (currentPage - 1) * pageSize + 1
  const endItem = Math.min(currentPage * pageSize, totalFilteredItems)
  const totalItems = totalFilteredItems

  return (
    <div className="space-y-4 border rounded-md relative">
      <div className="p-3 border-b">
        <div className="flex flex-col sm:flex-row gap-3 justify-between items-stretch sm:items-center">
          <div className="w-full sm:w-auto">
            <BillSearchInput
              value={searchQuery}
              onChange={onSearchChange}
              placeholder="Search by Bill Number or Title"
            />
          </div>
        </div>
      </div>

      {isMobile ? (
        // Mobile view -> use card layout
        <div className="p-4 grid grid-cols-1 gap-4">
          {bills.map((bill) => {
            const hasFullText = bill.full_text_available && bill.versions.some(v => v.text_extracted && v.text_s3_key)
            const hasAmendments = bill.versions.some(v => v.amendments_detected === true)
            const amendmentsProcessed = bill.versions.some(v => v.amendments_detected !== undefined)
            
            return (
              <div key={bill.bill_number} className="border rounded-lg p-4 space-y-3">
                <div className="flex justify-between items-start">
                  <h3 className="font-semibold text-lg">{bill.bill_number}</h3>
                  <span className="text-sm text-muted-foreground">{bill.session_code}</span>
                </div>
                <p className="text-sm text-muted-foreground">{bill.title}</p>
                {bill.synopsis && (
                  <p className="text-xs text-muted-foreground line-clamp-2">{bill.synopsis}</p>
                )}
                <div className="flex justify-between items-center text-xs text-muted-foreground">
                  <span>Data Source: {bill.data_source}</span>
                  <div className="flex items-center gap-2">
                    <span>Versions: {bill.versions.length}</span>
                    {hasAmendments && (
                      <span className="px-2 py-1 rounded-full text-xs bg-red-100 text-red-800">
                        Amendments
                      </span>
                    )}
                    {amendmentsProcessed && !hasAmendments && (
                      <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                        Clean
                      </span>
                    )}
                  </div>
                </div>
                {bill.crossfile_bill_number && (
                  <div className="text-xs text-muted-foreground">
                    Cross-file: {bill.crossfile_bill_number}
                  </div>
                )}
                
                {/* Mobile Actions */}
                <div className="flex items-center gap-2 pt-2 border-t">
                  {hasFullText && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewFullText(bill)}
                      className="flex-1"
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      View Full Text
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleViewVersions(bill)}
                    className="flex-1"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Versions
                  </Button>
                  {bill.pdf_url && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(bill.pdf_url, '_blank', 'noopener,noreferrer')}
                      className={hasFullText ? "" : "flex-1"}
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      PDF
                    </Button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      ) : (
        // Desktop view -> use DataTable
        <DataTable<DatasourceBill>
          columns={columns}
          data={bills}
          isLoading={isLoading}
          pageSize={pageSize}
          enablePagination={allBillsLoaded}
        />
      )}

      {/* Loading indicator */}
      {isLoading && (
        <div className="absolute inset-0 bg-background/50 flex items-center justify-center z-10">
          <div className="flex items-center gap-2 p-2 bg-background/90 rounded shadow">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Loading bills...</span>
          </div>
        </div>
      )}

      {/* Only show pagination controls when not using DataTable's internal pagination */}
      {!allBillsLoaded && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between px-4 py-2 border-t gap-4">
          <div className="text-sm text-muted-foreground">
            {isLoading 
              ? 'Loading bills...'
              : totalItems === 0
                ? 'No bills found'
                : `Showing ${startItem} to ${endItem} of ${totalItems}+ bills`}
            {searchQuery && !isLoading && ` (filtered)`}
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto overflow-x-auto pb-1">
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="icon"
                onClick={() => onPageChange(1)}
                disabled={currentPage === 1 || isLoading}
                aria-label="Go to first page"
              >
                <ChevronsLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => {
                  if (currentPage > 1) {
                    onPageChange(currentPage - 1)
                  }
                }}
                disabled={currentPage === 1 || isLoading}
                aria-label="Go to previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>

              {/* Page numbers */}
              <div className="flex items-center gap-1 mx-2 overflow-x-auto">
                {generateServerSidePageNumbers(currentPage, totalPages).map((pageNum, idx) =>
                  pageNum === '...' ? (
                    <span key={`ellipsis-${idx}`} className="px-2" aria-hidden="true">
                      ...
                    </span>
                  ) : (
                    <Button
                      key={pageNum}
                      variant={currentPage === pageNum ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => {
                        onPageChange(pageNum)
                      }}
                      disabled={isLoading}
                      className="min-w-[2rem] flex-shrink-0"
                      aria-label={`Go to page ${pageNum}`}
                      aria-current={currentPage === pageNum ? 'page' : undefined}
                    >
                      {pageNum}
                    </Button>
                  )
                )}
              </div>

              <Button
                variant="outline"
                size="icon"
                onClick={() => {
                  if (currentPage < totalPages) {
                    onPageChange(currentPage + 1)
                  }
                }}
                disabled={currentPage === totalPages || isLoading}
                aria-label="Go to next page"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Show summary when all bills are loaded */}
      {allBillsLoaded && (
        <div className="flex justify-between items-center px-4 py-2 border-t">
          <div className="text-sm text-muted-foreground">
            {totalItems === 0
              ? 'No bills found'
              : `Total: ${totalItems} bills${searchQuery ? ' (filtered)' : ''}`}
          </div>
          <div className="text-xs text-muted-foreground">
            Sorting and pagination handled by table
          </div>
        </div>
      )}

      {/* Full Text Dialog */}
      <BillFullTextDialog
        bill={selectedBillForFullText}
        open={fullTextDialogOpen}
        onOpenChange={handleCloseFullTextDialog}
      />

      {/* Versions Dialog */}
      <BillVersionsDialog
        bill={selectedBillForVersions}
        open={versionsDialogOpen}
        onOpenChange={handleCloseVersionsDialog}
      />
    </div>
  )
}


// Helper function to generate page numbers for server-side pagination
// Since we don't know the exact total pages, we show fewer page numbers
function generateServerSidePageNumbers(current: number, total: number): (number | '...')[] {
  const pages: (number | '...')[] = []

  // Always show current page
  pages.push(current)

  // Show previous page if it exists
  if (current > 1) {
    pages.unshift(current - 1)
  }

  // Show first page if not already included
  if (current > 2) {
    if (current > 3) {
      pages.unshift('...')
    }
    pages.unshift(1)
  }

  // Show next page if it exists (based on our estimate)
  if (current < total) {
    pages.push(current + 1)
  }

  return pages
}
